import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { Icon } from "semantic-ui-react";

import CommonModal from "../../components/CommonModal";

const splitName = (fullName) => {
  const cleaned = String(fullName ?? "").trim();
  if (!cleaned) return { firstName: "", lastName: "" };

  const parts = (cleaned.includes(",") ? cleaned.split(",") : cleaned.split(/\s+/))
    .map((p) => p.trim())
    .filter(Boolean);

  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

const buildLearnerKeyFromEnrollment = (enrollment) => {
  if (!enrollment?.id) return "";
  const { firstName, lastName } = splitName(enrollment?.name);
  return `${enrollment.id}:${firstName},${lastName}`;
};

const MultiClassSelectModal = ({ show, onClose, onConfirm, enrollments }) => {
  const rows = useMemo(() => (Array.isArray(enrollments) ? enrollments : []), [enrollments]);

  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (!show) return;
    // Start with nothing selected; user can choose any learners regardless of status.
    setSelectedIds([]);
  }, [show]);

  const allRowIds = useMemo(() => rows.map((r) => String(r?.id)).filter(Boolean), [rows]);

  const allSelected = allRowIds.length > 0 && selectedIds.length === allRowIds.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < allRowIds.length;

  const toggleAll = (checked) => {
    setSelectedIds(checked ? allRowIds : []);
  };

  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => {
      const set = new Set(prev);
      if (checked) set.add(id);
      else set.delete(id);
      return Array.from(set);
    });
  };

  const handleConfirm = () => {
    const selected = rows.filter((r) => selectedIds.includes(String(r?.id)));
    const learners = selected
      .map(buildLearnerKeyFromEnrollment)
      .filter((k) => typeof k === "string" && k.length > 0);
    if (typeof onConfirm === "function") onConfirm({ learners, selectedEnrollments: selected });
  };

  return (
    <CommonModal
      show={show}
      onHide={onClose}
      title={
        <Modal.Title>
          <Icon name="users" /> Select Learner(s) to Check-in - for Multi-Class-Select
        </Modal.Title>
      }
      footer={
        <>
          <Button variant="dark" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleConfirm} disabled={selectedIds.length === 0}>
            Select
          </Button>
        </>
      }
      modalProps={{
        size: "lg",
        centered: true,
        backdrop: "static",
        className: "manage-roster-modal",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-muted">
          {rows.length} learner{rows.length === 1 ? "" : "s"}
        </div>
      </div>

      <Table bordered hover responsive size="sm">
        <thead className="table-head bg-light">
          <tr>
            <th style={{ width: 48 }} className="text-center">
              <Form.Check
                type="checkbox"
                aria-label="Select all learners"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={(e) => toggleAll(e.target.checked)}
              />
            </th>
            <th style={{ width: 120 }}>ID</th>
            <th>First Name</th>
            <th>Last Name</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const id = String(r?.id ?? "");
            const { firstName, lastName } = splitName(r?.name);
            const checked = selectedIds.includes(id);
            return (
              <tr key={id}>
                <td className="text-center align-middle bg-light">
                  <Form.Check
                    type="checkbox"
                    aria-label={`Select learner ${r?.name ?? id}`}
                    checked={checked}
                    onChange={(e) => toggleOne(id, e.target.checked)}
                  />
                </td>
                <td className="align-middle">{id}</td>
                <td className="align-middle">{firstName}</td>
                <td className="align-middle">{lastName}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </CommonModal>
  );
};

export default MultiClassSelectModal;
